import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OptimizedImage from '../components/OptimizedImage';
import ThemeToggle from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const Auth = () => {
  const navigate = useNavigate();
  const { signup, login, loginWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const redirectAction = searchParams.get('redirect');
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Check invitation token on mount
  useEffect(() => {
    // Check URL param first
    if (inviteToken) {
      fetchInvitationInfo(inviteToken);
    } 
    // Check localStorage for invitation token (from email links)
    else if (redirectAction === 'invitation') {
      const storedToken = localStorage.getItem('invitation_token');
      if (storedToken) {
        fetchInvitationInfo(storedToken);
      }
    }
  }, [inviteToken, redirectAction]);

  const fetchInvitationInfo = async (token) => {
    try {
      const response = await axios.get(`${API}/invitations/${token}`);
      setInvitationInfo(response.data);
      setFormData(prev => ({ ...prev, email: response.data.email }));
      setIsLogin(response.data.user_exists);
      
      // Set to signup mode if redirect is from invitation email
      if (redirectAction === 'invitation' && !response.data.user_exists) {
        setIsLogin(false);
      }
      
      toast({
        title: response.data.user_exists ? 'Login to join workspace' : 'Create account to join workspace',
        description: `${response.data.invited_by} invited you to ${response.data.workspace_name} as ${response.data.role}`
      });
    } catch (error) {
      toast({
        title: 'Invalid invitation',
        description: error.response?.data?.detail || 'This invitation link is invalid or expired',
        variant: 'destructive'
      });
      navigate('/auth');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        if (!formData.name.trim()) {
          toast({ title: 'Please enter your name', variant: 'destructive' });
          setLoading(false);
          return;
        }
        result = await signup(formData.email, formData.password, formData.name);
      }

      if (result.success) {
        // Check for invitation token from URL or localStorage
        const tokenToUse = inviteToken || localStorage.getItem('invitation_token');
        
        // If there's an invitation token, accept it after authentication
        if (tokenToUse && invitationInfo) {
          try {
            const acceptResponse = await axios.post(
              `${API}/invitations/${tokenToUse}/accept`,
              {},
              { withCredentials: true }
            );
            
            // Clear localStorage token
            localStorage.removeItem('invitation_token');
            
            toast({ 
              title: `Welcome to ${acceptResponse.data.workspace_name}!`,
              description: `You've joined as ${acceptResponse.data.role}`
            });
            // Redirect to dashboard after accepting invitation
            navigate('/dashboard');
          } catch (inviteError) {
            // Auto-accept may have already happened during signup/login
            console.log('Invitation may have been auto-accepted:', inviteError);
            localStorage.removeItem('invitation_token');
            navigate('/dashboard');
          }
        } else {
          // No invitation - check if new signup or login
          if (isLogin) {
            toast({ title: 'Welcome back!' });
            navigate('/dashboard');
          } else {
            // New signup without invitation - redirect to onboarding
            toast({ title: 'Account created successfully!' });
            navigate('/onboarding');
          }
        }
      } else {
        toast({ title: result.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'An error occurred', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F5] to-white dark:from-[#0a0a0a] dark:to-[#0f0f0f] flex items-center justify-center p-6">
      {/* Theme Toggle - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <OptimizedImage 
            src="/doozum-logo.png" 
            alt="Doozum" 
            className="h-12 mx-auto mb-4"
            priority={true}
            width={240}
            height={48}
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isLogin ? 'Welcome back' : 'Get started'}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {isLogin ? 'Sign in to your account' : 'Create your Doozum account'}
          </p>
        </div>

        <Card>
          <CardHeader>
            {invitationInfo && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  🎉 You're invited to join <strong>{invitationInfo.workspace_name}</strong>!
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  {invitationInfo.invited_by} invited you as {invitationInfo.role}
                </p>
              </div>
            )}
            <CardTitle>{isLogin ? 'Sign In' : 'Sign Up'}</CardTitle>
            <CardDescription>
              {invitationInfo 
                ? (isLogin ? 'Sign in to join the workspace' : 'Create an account to join the workspace')
                : (isLogin ? 'Enter your credentials to access your boards' : 'Enter your details to create an account')
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Sign Up'
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white dark:bg-[#1a1a1a] px-2 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={loginWithGoogle}
              disabled={loading}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-1 text-[#1C3E53] hover:underline font-medium dark:text-[#4A90A4] dark:hover:text-[#5AA0B4]"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </CardFooter>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Auth;